/**
 * Source preview: a library item shown on its own in the viewer (clicked in the library), with its
 * own preview engine and playhead so the timeline's playhead and edits are not affected.
 */
import {
  addMedia,
  createClipFromMedia,
  createProject,
  insertClip,
  type MediaAsset,
  type Project,
  type ProjectSettings,
} from '@ve/core';
import type { PreviewEngine } from '@ve/engine';
import { create } from 'zustand';

interface SourcePreviewState {
  /** The library item being previewed; `null` ⇒ the viewer shows the timeline. */
  mediaId: string | null;
  engine: PreviewEngine | null;
  frame: number;
  playing: boolean;
  /** Length of the previewed item in frames. */
  duration: number;
}

export const useSourcePreview = create<SourcePreviewState>()(() => ({
  mediaId: null,
  engine: null,
  frame: 0,
  playing: false,
  duration: 0,
}));

export function openSourcePreview(mediaId: string): void {
  useSourcePreview.setState({ mediaId });
}

export function closeSourcePreview(): void {
  if (useSourcePreview.getState().mediaId === null) return;
  useSourcePreview.getState().engine?.pause();
  useSourcePreview.setState({ mediaId: null });
}

/** The source engine while a source preview is shown (transport commands then drive it). */
export function activeSourceEngine(): PreviewEngine | null {
  const { mediaId, engine } = useSourcePreview.getState();
  return mediaId !== null ? engine : null;
}

/** Connects a source engine to the store; returns a detach function. */
export function attachSourceEngine(engine: PreviewEngine): () => void {
  useSourcePreview.setState({ engine, frame: engine.frame, playing: engine.playing });
  const unsub = engine.subscribe(({ frame, playing }) => {
    const s = useSourcePreview.getState();
    if (s.frame !== frame || s.playing !== playing) useSourcePreview.setState({ frame, playing });
  });
  return () => {
    unsub();
    if (useSourcePreview.getState().engine === engine) useSourcePreview.setState({ engine: null });
  };
}

/** Even dimension within the project size limits (encoders and the schema need this). */
function dimension(px: number | undefined, fallback: number): number {
  if (!px) return fallback;
  return Math.min(8192, Math.max(16, Math.round(px / 2) * 2));
}

/**
 * A one-clip project that plays `asset` at its own aspect ratio, so the preview engine can show it
 * exactly as the timeline would (same decoder, proxies, audio mixer).
 */
export function sourceProject(asset: MediaAsset, settings: ProjectSettings): Project {
  const project = createProject(asset.name, {
    ...settings,
    width: dimension(asset.width, settings.width),
    height: dimension(asset.height, settings.height),
  });
  addMedia(project, structuredClone(asset));
  const seq = project.sequences[project.rootSequenceId]!;
  const track = asset.kind === 'audio' ? seq.audioTracks[0]! : seq.videoTracks[0]!;
  insertClip(project, track.id, createClipFromMedia(asset, project.settings, 0));
  return project;
}
